import { Trans, useTranslation } from "react-i18next";
import ErrorPage, { SUPPORT_EMAIL } from "./ErrorPage";
import NotFoundPage from "./NotFoundPage";
import { ACCESS_REASON } from "../../utils/accessError";

/**
 * The 403 a learner sees, worded for what they actually hit.
 *
 * `scope` picks the family of copy:
 *   "program" — a program (course) page they aren't registered for.
 *   "content" — a lesson / roleplay / info page. Registered learners get a way back to the program
 *               home plus the specific reason (draft -> coming soon, locked -> finish the previous
 *               scenarios); unregistered ones are told which program the content belongs to.
 *   "page"    — anything else (e.g. a learner opening a composer URL): a plain 403.
 *
 * A 404 payload short-circuits to the shared not-found page, so callers can hand every access
 * error to one component.
 */
const AccessErrorPage = ({ error, scope = "page" }) => {
  const { t } = useTranslation();

  if (!error) return null;
  if (error.status === 404) return <NotFoundPage />;

  const { reason, courseId, courseName, providerName, registered } = error;
  const programHome = courseId ? `/course/${courseId}` : null;

  const backHome = {
    key: "home",
    label: t("errors.actions.backHome"),
    to: "/",
    variant: "primary",
  };
  const backToProgram = programHome
    ? {
        key: "program",
        label: t("errors.actions.backToProgram"),
        to: programHome,
        variant: "primary",
      }
    : backHome;

  // "Contact the people who run the program" — the program's provider when we know it, plus the
  // Arrows inbox for learners who don't know who that is.
  const registrationDetails = (
    <>
      <div>
        {providerName
          ? t("errors.forbidden.contactProvider", { provider: providerName })
          : t("errors.forbidden.contactOwners")}
      </div>
      <div>
        <Trans
          i18nKey="errors.forbidden.supportFallback"
          values={{ email: SUPPORT_EMAIL }}
          components={{ mail: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> }}
        />
      </div>
    </>
  );

  if (scope === "program") {
    if (reason === ACCESS_REASON.LANGUAGE) {
      return (
        <ErrorPage
          code="403"
          title={t("errors.forbidden.language.title")}
          message={t("errors.forbidden.language.body")}
          actions={[backHome]}
        />
      );
    }
    if (reason === ACCESS_REASON.DRAFT) {
      return (
        <ErrorPage
          code="403"
          title={t("errors.forbidden.draft.title")}
          message={t("errors.forbidden.draft.body")}
          actions={[backHome]}
        />
      );
    }
    return (
      <ErrorPage
        code="403"
        title={t("errors.forbidden.program.title")}
        message={
          courseName
            ? t("errors.forbidden.program.bodyNamed", { program: courseName })
            : t("errors.forbidden.program.body")
        }
        details={registrationDetails}
        actions={[backHome]}
      />
    );
  }

  if (scope === "content") {
    if (!registered) {
      return (
        <ErrorPage
          code="403"
          title={t("errors.forbidden.otherProgram.title")}
          message={
            courseName
              ? t("errors.forbidden.otherProgram.bodyNamed", { program: courseName })
              : t("errors.forbidden.otherProgram.body")
          }
          details={registrationDetails}
          actions={[backHome]}
        />
      );
    }
    if (reason === ACCESS_REASON.DRAFT) {
      return (
        <ErrorPage
          code="403"
          title={t("errors.forbidden.draft.title")}
          message={t("errors.forbidden.draft.body")}
          actions={[backToProgram]}
        />
      );
    }
    if (reason === ACCESS_REASON.LOCKED) {
      return (
        <ErrorPage
          code="403"
          title={t("errors.forbidden.locked.title")}
          message={t("errors.forbidden.locked.body")}
          actions={[backToProgram]}
        />
      );
    }
    if (reason === ACCESS_REASON.REFLECTION_SOURCE) {
      return (
        <ErrorPage
          code="403"
          title={t("errors.forbidden.reflectionSource.title")}
          message={t("errors.forbidden.reflectionSource.body")}
          actions={[backToProgram]}
        />
      );
    }
    if (reason === ACCESS_REASON.REQUIRED_INFO) {
      // The questions are answered on the program page (CourseItem's inline form), so that's where
      // this sends them.
      return (
        <ErrorPage
          code="403"
          title={t("errors.forbidden.requiredInfo.title")}
          message={t("errors.forbidden.requiredInfo.body")}
          actions={[backToProgram]}
        />
      );
    }
    if (reason === ACCESS_REASON.LANGUAGE) {
      return (
        <ErrorPage
          code="403"
          title={t("errors.forbidden.language.title")}
          message={t("errors.forbidden.language.body")}
          actions={[backToProgram]}
        />
      );
    }
    return (
      <ErrorPage
        code="403"
        title={t("errors.forbidden.content.title")}
        message={t("errors.forbidden.content.body")}
        actions={[backToProgram]}
      />
    );
  }

  return (
    <ErrorPage
      code="403"
      title={t("errors.forbidden.generic.title")}
      message={t("errors.forbidden.generic.body")}
      actions={[backHome]}
    />
  );
};

export default AccessErrorPage;
